import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import EmailIcon from "@/assets/images/Email.png";
import linkedin from "@/assets/svg/linkedin-circular.svg";
import crm from "@/assets/images/crm.png";
import ConnectionCard from "./ConnectionCard";
import { getConnectedChannelsMap } from "../../../utils/helperFunctions";
import whatsappIcon from "@/assets/svg/WhatsApp-logo.svg";
import { useConnectOutlook, useDeleteChannel, useReconnectChannel, useUnipileConnect } from "@/hooks/useIntegration";
import { showError } from "@/shared/utils/toast";
import { useQueryClient } from "@tanstack/react-query";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import { Link as LinkIcon, X } from "lucide-react";
import gmailIcon from "@/assets/svg/gmail.svg";
import { MicrosoftLogo } from "@/assets/svg/MicrosoftLogo";
import mailIcon from "@/assets/svg/mail.svg";

const EMAIL_CHANNELS = new Set(["outlook", "google_oauth", "mail", "imap"]);
const WHATSAPP_CHANNELS = new Set(["whatsapp"]);
const LINKEDIN_CHANNELS = new Set(["linkedin"]);

const normalize = (value) => String(value || "").trim().toLowerCase();

const accountLabel = (account) => {
  const channel = normalize(account?.channel || account?.provider);
  if (EMAIL_CHANNELS.has(channel) && account?.email) return account.email;
  if (WHATSAPP_CHANNELS.has(channel) && account?.phone) return account.phone;
  if (LINKEDIN_CHANNELS.has(channel) && account?.username) return `@${account.username.replace(/^@/, "")}`;
  const base = account?.label || account?.email || account?.provider || account?.channel || "Account";
  return base;
};

const accountReference = (account) => {
  if (!account?.unipile_account_id) return null;
  return String(account.unipile_account_id).slice(-6);
};

const AccountConnections = ({ myChannels }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const accountId = searchParams.get("account_id");
  const channels = useMemo(() => myChannels?.result || [], [myChannels?.result]);
  const connectedMap = getConnectedChannelsMap(channels);
  const { mutate: connectOutlook, isPending: isConnectingEmail } = useConnectOutlook();
  const { mutate: unipileConnect } = useUnipileConnect();
  const { mutate: deleteChannel } = useDeleteChannel();
  const { mutate: reconnectChannel } = useReconnectChannel();
  const [deletingChannelId, setDeletingChannelId] = useState(null);
  const [reconnectingChannelId, setReconnectingChannelId] = useState(null);
  const [showEmailProviderModal, setShowEmailProviderModal] = useState(false);
  const hasProcessedUnipileConnect = useRef(false);
  const emailConnectOptions = [
    {
      label: "Gmail",
      description: "Connect with Google OAuth",
      provider: "GOOGLE_OAUTH",
      icon: <img src={gmailIcon} alt="Gmail" className="w-5 h-5 object-contain" />,
    },
    {
      label: "Microsoft",
      description: "Connect Outlook or Microsoft 365",
      provider: "OUTLOOK",
      icon: <MicrosoftLogo size={20} />,
    },
    {
      label: "IMAP",
      description: "Connect any email with IMAP",
      provider: "MAIL",
      icon: <img src={mailIcon} alt="IMAP" className="w-5 h-5 object-contain" />,
    },
  ];
  const accountGroups = useMemo(() => ({
    email: channels.filter((channel) =>
      EMAIL_CHANNELS.has(normalize(channel.channel)) ||
      EMAIL_CHANNELS.has(normalize(channel.provider)),
    ),
    whatsapp: channels.filter((channel) =>
      normalize(channel.channel) === "whatsapp" || normalize(channel.provider) === "whatsapp",
    ),
    linkedin: channels.filter((channel) =>
      normalize(channel.channel) === "linkedin" || normalize(channel.provider) === "linkedin",
    ),
  }), [channels]);

  // After Unipile Hosted Auth redirect
  useEffect(() => {
    if (
      accountId &&
      !hasProcessedUnipileConnect.current
    ) {
      hasProcessedUnipileConnect.current = true;
      unipileConnect(
        { accountId },
        {
          onSettled: () => {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete("account_id");
              return Object.fromEntries(next) || {};
            });
          },
        },
      );
    }
  }, [
    accountId,
    unipileConnect,
    setSearchParams,
  ]);

  const openHostedAuth = ({ provider, label, onBeforeRedirect }) => {
    connectOutlook({ provider }, {
      onSuccess: (data) => {
        const authUrl = data?.authUrl ?? data?.url;
        if (authUrl && typeof authUrl === "string") {
          onBeforeRedirect?.();
          queryClient.invalidateQueries({ queryKey: ["my-channels"] });
          window.location.href = authUrl;
        } else {
          showError(`Unable to get ${label} auth URL. Please try again.`);
        }
      },
    });
  };

  const toggleConnection = (account) => {
    switch (account) {
      case "linkedin":
        openHostedAuth({ provider: "LINKEDIN", label: "LinkedIn" });
        break;
      case "whatsapp":
        openHostedAuth({ provider: "WHATSAPP", label: "WhatsApp" });
        break;
      case "outlook": {
        setShowEmailProviderModal(true);
        break;
      }
      case "google_oauth": {
        openHostedAuth({ provider: "GOOGLE_OAUTH", label: "Google" });
        break;
      }
      case "mail": {
        openHostedAuth({ provider: "MAIL", label: "IMAP" });
        break;
      }
      default:
        break;
    }
  };

  const handleEmailProviderSelect = ({ provider, label }) => {
    openHostedAuth({
      provider,
      label,
      onBeforeRedirect: () => setShowEmailProviderModal(false),
    });
  };

  const handleDisconnect = (channelAccount) => {
    if (!channelAccount?.id) return;
    if (!window.confirm("Disconnect this account from SalesWorx?")) return;

    setDeletingChannelId(channelAccount.id);
    deleteChannel(channelAccount.id, {
      onSettled: () => setDeletingChannelId(null),
    });
  };

  const handleReconnect = (channelAccount) => {
    if (!channelAccount?.id) return;
    setReconnectingChannelId(channelAccount.id);
    reconnectChannel(channelAccount.id, {
      onSuccess: (data) => {
        const authUrl = data?.authUrl ?? data?.url;
        if (authUrl && typeof authUrl === "string") {
          window.location.href = authUrl;
        } else {
          showError("Unable to get reconnect URL. Please try again.");
        }
      },
      onSettled: () => setReconnectingChannelId(null),
    });
  };

  const renderAccountList = (accounts, channelLabel) => (
    <div className="flex flex-col gap-2">
      {accounts.length === 0 ? (
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/60">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-gray-400 shadow-sm dark:bg-gray-900 dark:text-gray-500">
            0
          </span>
          <p className="text-xs text-gray-500 dark:text-gray-400">No {channelLabel} account connected yet.</p>
        </div>
      ) : accounts.map((account) => {
        const status = normalize(account.status);
        const needsReconnect = status === "reconnectrequired";
        const isBusy = deletingChannelId === account.id || reconnectingChannelId === account.id;
        const reference = accountReference(account);
        const statusLabel = account.status || "Unknown";
        return (
          <div
            key={account.id}
            className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-800 dark:bg-gray-800/70 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">{accountLabel(account)}</p>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <span>{account.provider || account.channel}</span>
                <span
                  className={`rounded-full px-2 py-0.5 font-semibold tracking-normal ${
                    status === "active"
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
                      : needsReconnect
                        ? "bg-orange-50 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                  }`}
                >
                  {statusLabel}
                </span>
                {reference && <span className="normal-case tracking-normal text-gray-400 dark:text-gray-500">ID {reference}</span>}
              </div>
            </div>
            <div className="flex shrink-0 gap-2 sm:pl-4">
              {needsReconnect && (
                <button
                  type="button"
                  className="rounded-lg bg-orange-500 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
                  onClick={() => handleReconnect(account)}
                  disabled={isBusy}
                >
                  {reconnectingChannelId === account.id ? "Opening..." : "Reconnect"}
                </button>
              )}
              <button
                type="button"
                  className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 dark:border-red-900/70 dark:text-red-300 dark:hover:bg-red-950/30 disabled:opacity-60"
                onClick={() => handleDisconnect(account)}
                disabled={isBusy}
              >
                {deletingChannelId === account.id ? "Disconnecting..." : "Disconnect"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderChannelSection = ({ accountType, title, description, logoSrc, altText, accounts, channelLabel }) => (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="flex flex-col gap-4 border-b border-gray-100 bg-gray-50/70 px-5 py-4 dark:border-gray-800 dark:bg-gray-800/50 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white p-2 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <img src={logoSrc} alt={altText} className="h-full w-full object-contain" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
          </div>
        </div>
        <button
          type="button"
          className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-800 transition-colors hover:border-secondary hover:bg-secondary/5 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          onClick={() => toggleConnection(accountType)}
          disabled={accountType === "outlook" && isConnectingEmail}
        >
          <LinkIcon size={15} strokeWidth={2.5} />
          {accountType === "outlook" && isConnectingEmail ? "Opening..." : "Connect account"}
        </button>
      </div>
      <div className="space-y-3 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {accounts.length} connected {accounts.length === 1 ? "account" : "accounts"}
          </p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500">Reusable across campaigns</p>
        </div>
        {renderAccountList(accounts, channelLabel)}
      </div>
    </section>
  );

  return (
    <div className="mx-auto flex w-full flex-col gap-4">
      <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-xs text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-100 sm:px-5">
        <p className="font-semibold">Connect each account once</p>
        <p className="mt-1 text-blue-800/75 dark:text-blue-200/75">
          Connected accounts can be selected as senders when you configure a campaign.
        </p>
      </div>

      {renderChannelSection({
        accountType: "outlook",
        title: "Email accounts",
        description: "Connect Gmail, Microsoft 365, Outlook, or IMAP mailboxes.",
        logoSrc: EmailIcon,
        altText: "Email",
        accounts: accountGroups.email,
        channelLabel: "email",
      })}
      {renderChannelSection({
        accountType: "whatsapp",
        title: "WhatsApp accounts",
        description: "Connect each WhatsApp number you want to use for campaigns.",
        logoSrc: whatsappIcon,
        altText: "WhatsApp",
        accounts: accountGroups.whatsapp,
        channelLabel: "WhatsApp",
      })}
      {renderChannelSection({
        accountType: "linkedin",
        title: "LinkedIn accounts",
        description: "Connect each LinkedIn profile and choose it per campaign.",
        logoSrc: linkedin,
        altText: "LinkedIn",
        accounts: accountGroups.linkedin,
        channelLabel: "LinkedIn",
      })}

      <ConnectionCard
        logoSrc={crm}
        altText="CRM"
        title="Integrate CRM"
        description="Connect your current CRM platform to directly integrate customer information."
        accountType="crm"
        isConnected={connectedMap.crm}
        onToggleConnection={toggleConnection}
        comingSoon={true}
      />

      <CustomModal
        isOpen={showEmailProviderModal}
        width="430px"
        className="p-6 rounded-xl"
      >
        <div className="flex flex-col gap-5">
          <div className="flex items-start justify-between gap-4">
            <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  Choose email provider
                </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select how you want to connect your email account.
              </p>
            </div>
            <button
              type="button"
              className="rounded-full p-1 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
              onClick={() => setShowEmailProviderModal(false)}
              disabled={isConnectingEmail}
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex flex-col gap-3">
            {emailConnectOptions.map((option) => (
              <button
                key={option.provider}
                type="button"
                className="flex w-full items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 text-left transition-colors hover:border-secondary hover:bg-secondary/5 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/70 disabled:opacity-60"
                onClick={() => handleEmailProviderSelect(option)}
                disabled={isConnectingEmail}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-gray-200 bg-white p-1.5 dark:border-gray-700 dark:bg-gray-900">
                  {option.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {option.label}
                  </span>
                  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                    {option.description}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default AccountConnections;
